import { useEffect, useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import Carousel from '@/components/carousel/Carousel';
import VideoPlayer from '@/components/video';
import { CAROUSEL_HEIGHT, IMAGE_HEIGHT } from '@/constants/mediaDisplay';
import { useAppSelector } from '@/hooks/redux';
import { Posts } from '@/models/posts';
import { Violations } from '@/models/violations';
import { getMonthsSince } from '@/utils/datesAndTime/datesAndTime';

import FullscreenImageViewer from './_partials/FullscreenImageViewer';
import PostIdentificationMarkers from './_partials/PostIdentificationMarkers';

interface MediaDisplayProps {
  data: Posts.Details | Violations.Details;
  showIdentificationMarkers?: boolean;
  markerItems?: Posts.MarkerItem[];
  videoPlaybackStart?: number;
  videoLastClick?: string | number;
  filter?: string;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  onVideoProgress?: (seconds: number) => void;
  onMarkersReset?: () => void;
}

const MediaDisplay = ({
  data,
  showIdentificationMarkers = false,
  markerItems = [],
  videoPlaybackStart,
  videoLastClick,
  filter = '',
  activeIndex = 0,
  setActiveIndex,
  onVideoProgress,
  onMarkersReset
}: MediaDisplayProps) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isPlaying, setIsPlaying] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const user = useAppSelector((state) => state.user.currentUser);
  const generalTerm = user?.workspaceStorageTerms?.general;
  const ViolationTerm = user?.workspaceStorageTerms?.violation;

  const handleLoad = () => {
    if (!data) return;
    const element = document.getElementById(data.media[activeIndex]?.mediaPath);
    if (element) {
      const { clientWidth: width, clientHeight: height } = element;
      if (width || height) setDimensions({ width, height });
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    onMarkersReset?.();
  };

  const handleIndexChange = (index: number) => {
    setActiveIndex(index);
    onMarkersReset?.();
  };

  const handleImageClick = (imagePath: string) => {
    setFullscreenImage(imagePath);
  };

  const handleCloseFullscreen = () => {
    setFullscreenImage(null);
  };

  useEffect(() => {
    if (videoPlaybackStart !== undefined && showIdentificationMarkers) {
      setIsPlaying(false);
    }
  }, [videoPlaybackStart, showIdentificationMarkers]);

  useEffect(() => {
    const handleResize = () => {
      // Force recalculation of dimensions
      handleLoad();
      // Force a re-render of markers by creating a new object reference
      setDimensions((prev) => ({ ...prev }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeIndex, data]); // Add dependencies to ensure correct media is targeted

  if (!data) return <Typography variant="body2">Data not available</Typography>;

  const isViolation =
    data.evaluation?.compliance?.compliance === 'violation' ||
    (data as Violations.Details).violations?.some(
      (v) => v.compliance === 'violation'
    );

  const dataRetentionTerm = isViolation
    ? getMonthsSince(ViolationTerm)
    : getMonthsSince(generalTerm);

  const renderMedia = ({
    item,
    isExpired,
    isCarousel = false
  }: {
    item: Posts.Media;
    isExpired: boolean;
    isCarousel?: boolean;
  }) => {
    if (isExpired) {
      return (
        <Box
          bgcolor="text.disabled"
          alignContent="center"
          textAlign="center"
          height={IMAGE_HEIGHT}
        >
          <Typography variant="body1" color="text.secondary">
            {`This post has expired, as per your workspace's data retention term of ${dataRetentionTerm} months.`}
          </Typography>
        </Box>
      );
    }

    const isVideo = item?.mediaType === 'video' && !isExpired;
    const isYoutubeContent =
      data.platform === 'youtube-video' ||
      data.platform === 'youtube-short' ||
      data.platform === 'youtube-livestream';

    if (isVideo) {
      return (
        <Stack
          id="detail-media"
          alignItems="center"
          display={isYoutubeContent ? 'block' : 'flex'}
        >
          <Stack style={{ position: 'relative' }}>
            <VideoPlayer
              url={item.mediaPath}
              seek={videoPlaybackStart}
              lastClick={videoLastClick}
              platform={data.platform}
              onReady={handleLoad}
              onPlay={handlePlay}
              onProgress={(video) => onVideoProgress?.(video.playedSeconds)}
              playing={isPlaying}
              isCarousel={isCarousel}
            />
            {showIdentificationMarkers && (
              <PostIdentificationMarkers
                items={markerItems}
                dimensions={dimensions}
              />
            )}
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack id="detail-media" alignItems="center" display="block">
        <Stack style={{ position: 'relative', justifyContent: 'center' }}>
          <div
            id="image-container"
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              height: isCarousel ? CAROUSEL_HEIGHT : IMAGE_HEIGHT
            }}
          >
            <img
              id={item.mediaPath}
              src={item.mediaPath}
              alt={data.url}
              style={{
                filter,
                objectFit: 'contain',
                cursor: 'pointer'
              }}
              onLoad={handleLoad}
              onClick={() => handleImageClick(item.mediaPath)}
            />
          </div>
          {showIdentificationMarkers && (
            <PostIdentificationMarkers
              items={markerItems}
              dimensions={dimensions}
            />
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      {data.media.length > 1 ? (
        <Carousel
          cycleNavigation={false}
          autoPlay={false}
          activeIndex={activeIndex}
          onChangeVisibleItem={handleIndexChange}
          items={data.media.map((_, idx) => (
            <div
              key={`${data.media[idx].mediaPath}${data.url}${idx}`}
              style={{ display: activeIndex === idx ? 'block' : 'none' }}
            >
              {activeIndex === idx &&
                renderMedia({
                  item: data.media[idx],
                  isExpired: data.isExpired,
                  isCarousel: true
                })}
              {/* Render only active media */}
            </div>
          ))}
          id={Number(data.url)}
          sx={{
            height: CAROUSEL_HEIGHT
          }}
        />
      ) : (
        renderMedia({
          item: data.media[activeIndex],
          isExpired: data.isExpired
        })
      )}
      <FullscreenImageViewer
        open={fullscreenImage !== null}
        imageUrl={fullscreenImage || ''}
        onClose={handleCloseFullscreen}
      />
    </>
  );
};

export default MediaDisplay;
